

export const frontMatter = {};
export const contentTitle = 'Building Apps';
export {default as metadata} from '@site/.docusaurus/docusaurus-plugin-content-docs/default/site-docs-developer-guide-building-apps-md-2af.json'
export const assets = {

};

import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const toc = [{
  "value": "Package Format",
  "id": "package-format",
  "level": 2
}, {
  "value": "Known issues",
  "id": "known-issues",
  "level": 3
}, {
  "value": "Bundle Size",
  "id": "bundle-size",
  "level": 2
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    header: "header",
    li: "li",
    p: "p",
    table: "table",
    tbody: "tbody",
    td: "td",
    th: "th",
    thead: "thead",
    tr: "tr",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.header, {
      children: _jsx(_components.h1, {
        id: "building-apps",
        children: "Building Apps"
      })
    }), "\n", _jsx(_components.p, {
      children: "This article discusses considerations in building and deploying applications that contain deck.gl."
    }), "\n", _jsx(_components.h2, {
      id: "package-format",
      children: "Package Format"
    }), "\n", _jsxs(_components.p, {
      children: ["Starting from v9.0, deck.gl is fully ", _jsx(_components.a, {
        href: "https://nodejs.org/api/packages.html",
        children: "ES module"
      }), " compliant with support for both ESM-style ", _jsx(_components.code, {
        children: "import"
      }), " and CommonJS-style ", _jsx(_components.code, {
        children: "require()"
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "When installed from npm, each submodule provides the following entry points:"
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Entry"
          }), _jsx(_components.th, {
            children: "Type"
          }), _jsx(_components.th, {
            children: "Description"
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: _jsx(_components.code, {
              children: "dist/index.js"
            })
          }), _jsx(_components.td, {
            children: "ESM (import)"
          }), _jsx(_components.td, {
            children: "Code is only lightly transpiled to target ES2020. Tree-shakable."
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: _jsx(_components.code, {
              children: "dist/index.cjs"
            })
          }), _jsx(_components.td, {
            children: "CommonJS (require)"
          }), _jsx(_components.td, {
            children: "Code is bundled without dependencies, and transpiled to target Node16. Not tree-shakable."
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: _jsx(_components.code, {
              children: "dist.min.js"
            })
          }), _jsx(_components.td, {
            children: "UMD (script tag)"
          }), _jsxs(_components.td, {
            children: ["Code is bundled with dependencies, and transpiled to target ", _jsx(_components.code, {
              children: "['chrome110', 'firefox110', 'safari15']"
            }), " then minified."]
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: _jsx(_components.code, {
              children: "dist/dist.dev.js"
            })
          }), _jsx(_components.td, {
            children: "UMD (script tag)"
          }), _jsx(_components.td, {
            children: "Same as above, but not minified."
          })]
        })]
      })]
    }), "\n", _jsx(_components.p, {
      children: "Although the packages are designed to work with the widest range of use cases, it's going to be much easier if you work with an up-to-date development framework."
    }), "\n", _jsx(_components.h3, {
      id: "known-issues",
      children: "Known issues"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Some older bundlers may not support the latest syntax featuers (e.g. Webpack 4 does not recognize optional chaining). You need to use a Babel plugin  and tell it to include ", _jsx(_components.code, {
          children: "node_modules"
        }), " with ", _jsx(_components.code, {
          children: "@babel/preset-ev"
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["Frameworks such as ", _jsx(_components.code, {
          children: "Next.js"
        }), " and ", _jsx(_components.code, {
          children: "Gatsby"
        }), " leverage Server Side Rendering to improve page loading performance. For projects that do not use ", _jsx(_components.code, {
          children: "type: \"module\""
        }), " in their package.json, SSR may fail with an error message ", _jsx(_components.code, {
          children: "Error: require() of ES Module 'xxx'"
        }), ". This is because some of deck.gl's upstream dependencies, such as ", _jsx(_components.code, {
          children: "d3"
        }), ", have opted to become ESM-only and no longer support ", _jsx(_components.code, {
          children: "require()"
        }), ". See ", _jsx(_components.a, {
          href: "https://github.com/visgl/deck.gl/issues/7735",
          children: "possible solutions"
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["Although enormous efforts have been put into converting the deck.gl and its upstream libraries' code base into TypeScript, some part of the legacy code paths may not meet strict type requirements, such as ", _jsx(_components.code, {
          children: "noImplicitAny"
        }), " and ", _jsx(_components.code, {
          children: "strictNullChecks"
        }), ". You may need to set ", _jsx(_components.code, {
          children: "skipLibCheck: true"
        }), " in your project's ", _jsx(_components.code, {
          children: "tsconfig"
        }), " to unblock compilation."]
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      id: "bundle-size",
      children: "Bundle Size"
    }), "\n", _jsx(_components.p, {
      children: "deck.gl provides a lot of functionality and the amount of code these libraries contain will unsurprisingly impact the size of your application bundle and your startup load time."
    }), "\n", _jsx(_components.p, {
      children: "deck.gl is designed from the ground up to be highly extensible. Visualization types are supported by different layers; additional layer features can be added by layer extensions; more data formats can be supported by loaders.gl submodules. The core is fairly lean, and each functionality is self-contained, so that applications do not have to bundle things that they don't need. Because modern build tools support tree shaking, most new features added do not have a visible size impact on existing applications."
    }), "\n", _jsxs(_components.p, {
      children: ["Starting with v9.4, applications that only target WebGL2 can further reduce bundle size by configuring their bundler to resolve the custom ", _jsxs(_components.a, {
        href: "https://nodejs.org/api/packages.html#conditional-exports",
        children: [_jsx(_components.code, {
          children: "package.json"
        }), " export condition"]
      }), " ", _jsx(_components.code, {
        children: "visgl:webgl-only"
      }), ". Packages that contain WebGPU implementations, including ", _jsx(_components.code, {
        children: "@deck.gl/core"
      }), ", ", _jsx(_components.code, {
        children: "@deck.gl/layers"
      }), ", and the ", _jsx(_components.code, {
        children: "@deck.gl/*-layers"
      }), " packages, then resolve to builds with WebGPU branches and WGSL shader sources removed while preserving their normal public API and tree-shaking behavior. The default export remains WebGPU-enabled, so applications that use WebGPU should not enable this condition. Refer to your build tool's documentation for adding custom conditions: ", _jsxs(_components.a, {
        href: "https://esbuild.github.io/api/#conditions",
        children: ["esbuild ", _jsx(_components.code, {
          children: "conditions"
        })]
      }), ", ", _jsxs(_components.a, {
        href: "https://vite.dev/config/shared-options.html#resolve-conditions",
        children: ["Vite ", _jsx(_components.code, {
          children: "resolve.conditions"
        })]
      }), " (also available in ", _jsx(_components.a, {
        href: "https://vitest.dev/config/",
        children: "Vitest through Vite configuration"
      }), "), ", _jsxs(_components.a, {
        href: "https://github.com/rollup/plugins/tree/master/packages/node-resolve#exportconditions",
        children: ["Rollup ", _jsx(_components.code, {
          children: "exportConditions"
        })]
      }), ", ", _jsxs(_components.a, {
        href: "https://webpack.js.org/configuration/resolve/#resolveconditionnames",
        children: ["webpack ", _jsx(_components.code, {
          children: "resolve.conditionNames"
        })]
      }), ", or ", _jsxs(_components.a, {
        href: "https://rspack.dev/config/resolve.html#resolveconditionnames",
        children: ["Rspack ", _jsx(_components.code, {
          children: "resolve.conditionNames"
        })]
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "deck.gl maintainers are conscious about how design decisions and code changes impact bundle size. The test harness has a script that evaluates the size of a minified bundle after each build. The following numbers are offered for your reference."
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Imports"
          }), _jsx(_components.th, {
            children: "Bundle size"
          }), _jsx(_components.th, {
            children: "Compressed"
          }), _jsx(_components.th, {
            children: "Bundle size (WebGL only)"
          }), _jsx(_components.th, {
            children: "Compressed (WebGL only)"
          }), _jsx(_components.th, {
            children: "Comments"
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Deck + Layer"
          }), _jsx(_components.td, {
            children: "504.9 kb"
          }), _jsx(_components.td, {
            children: "146.8 kb"
          }), _jsx(_components.td, {
            children: "493.6 kb"
          }), _jsx(_components.td, {
            children: "144.5 kb"
          }), _jsx(_components.td, {
            children: "Minimal core; baseline"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "DeckGL (React)"
          }), _jsx(_components.td, {
            children: "13.6 kb"
          }), _jsx(_components.td, {
            children: "5.2 kb"
          }), _jsx(_components.td, {
            children: "13.6 kb"
          }), _jsx(_components.td, {
            children: "5.2 kb"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "HexagonLayer"
          }), _jsx(_components.td, {
            children: "50.9 kb"
          }), _jsx(_components.td, {
            children: "14.4 kb"
          }), _jsx(_components.td, {
            children: "40.2 kb"
          }), _jsx(_components.td, {
            children: "12.5 kb"
          }), _jsx(_components.td, {})]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "GeoJsonLayer"
          }), _jsx(_components.td, {
            children: "167.4 kb"
          }), _jsx(_components.td, {
            children: "46.9 kb"
          }), _jsx(_components.td, {
            children: "129.6 kb"
          }), _jsx(_components.td, {
            children: "39.5 kb"
          }), _jsxs(_components.td, {
            children: ["Includes the most commonly used primitive layers:", _jsx("br", {}), " ScatterplotLayer, IconLayer, TextLayer, PathLayer, PolygonLayer"]
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "MVTLayer"
          }), _jsx(_components.td, {
            children: "283.4 kb"
          }), _jsx(_components.td, {
            children: "82.1 kb"
          }), _jsx(_components.td, {
            children: "245.6 kb"
          }), _jsx(_components.td, {
            children: "74.6 kb"
          }), _jsx(_components.td, {
            children: "GeoJsonLayer + TileLayer + MVT loader"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "Tile3DLayer"
          }), _jsx(_components.td, {
            children: "355.4 kb"
          }), _jsx(_components.td, {
            children: "106.1 kb"
          }), _jsx(_components.td, {
            children: "343.9 kb"
          }), _jsx(_components.td, {
            children: "103.7 kb"
          }), _jsx(_components.td, {
            children: "ScenegraphLayer + SimpleMeshLayer + GLTF loader + 3D tiles loader"
          })]
        })]
      })]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Numbers measured using v9.4.0-alpha.2."
      }), "\n", _jsxs(_components.li, {
        children: ["Each row is an independent bundle that exposes only the listed class or classes. Rows after the first externalize ", _jsx(_components.code, {
          children: "@deck.gl/core"
        }), " and its direct ", _jsx(_components.code, {
          children: "@luma.gl/*"
        }), " dependencies to show their incremental impact on an application that already includes the core bundle."]
      }), "\n", _jsxs(_components.li, {
        children: ["Bundled and minified by esbuild targeting evergreen browsers using ", _jsx(_components.code, {
          children: "--tsconfig=test/size/tsconfig.json"
        }), ". The WebGL-only columns additionally use ", _jsx(_components.code, {
          children: "--conditions=visgl:webgl-only"
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["Compressed bundle sizes are calculated using ", _jsx(_components.code, {
          children: "gzip -9"
        }), ". Consider using slower ", _jsx(_components.code, {
          children: "brotli"
        }), " compression for static assets, it typically provides an additional 20% reduction."]
      }), "\n"]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}

